import { Injectable } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { first, map, shareReplay, switchMap } from 'rxjs/operators';

import { APIService, KPI } from '../../@core/api';
import { idMapToArray, ScopeService } from './scope.service';
import { UsersService } from './users.service';

@Injectable()
export class AssetNotationsService {

  constructor(
    private api: APIService,
    private scope: ScopeService,
    private usersService: UsersService,
  ) {
  }

  /**
   * Returns an Observable emitting the assset notations for the specified assets.
   *
   * @param assets A list of assets.
   */
  public loadInvestmentNotationsByAssets(
    assets: KPI.Asset[],
  ): Observable<KPI.MappedAssetNotation[]> {
    if (assets.length > 0) {
      return this.api.audit_asset_notations(
        {
          assets: assets.map(({id}) => id).join(','),
        },
        true,
      ).pipe(
        switchMap((assetNotations) => 
          combineLatest(assetNotations.map((assetNotation) => this.mapAssetNotation(assetNotation)))
        ),
      );
    }

    return of([]);
  }

  private allRawAssetNotations: Observable<KPI.AssetNotation[]>;
  public loadAllRawAssetsNotations(): Observable<KPI.AssetNotation[]> {
    if (!this.allRawAssetNotations) {
      this.allRawAssetNotations = this.api.audit_asset_notations(
        {},
        true,
      ).pipe(shareReplay(1));
    }
    return this.allRawAssetNotations;
  }

  private mapAssetNotation(assetNotation: KPI.AssetNotation): Observable<KPI.MappedAssetNotation> {
    return new Observable((observer) => {
      this.scope.auditQuestions$
        .pipe(
          first(),
          idMapToArray(),
        )
        .subscribe((auditQuestions) => {
          let auditQuestion: KPI.AuditQuestion;
          let auditQuestionItem: KPI.AuditQuestionItem;
          for (auditQuestion of auditQuestions) {
            auditQuestionItem = auditQuestion.items.find(
              (item) => item.id === assetNotation.question_item,
            );
            if (auditQuestionItem) {
              break;
            }
          }

          observer.next({
            ...assetNotation,
            audit_question_object: auditQuestion,
            audit_question_item_object: auditQuestionItem,
          });
          observer.complete();
        });
    });
  }

  public getLastAssetsEvaluation(assets: KPI.Asset[]): Observable<{[assetId: number]: KPI.MappedAssetNotation}> {
    return this.loadInvestmentNotationsByAssets(assets).pipe(
      first(),
      map((notations) => {
        const lastEvaluations = {}
        assets.map((asset) => {
          const evaluations = notations.filter((n) =>
            n.audit_question_object.mnemonic === 'technical_state'
            && n.asset === asset.id,
          ).sort((a, b) => a.audit_year - b.audit_year);
          lastEvaluations[asset.id] = evaluations.pop();
        });
        return lastEvaluations;
      })
    )
  }

  public ratingReasonsForAsset$(perimeterId: number, assetId: number): Observable<{ name: string, year: number }[]> {
    return this.api.audit_rating_reasons_for_asset(perimeterId, assetId);
  }

  public getTechnicalStateNotes(assets: KPI.Asset[]): Observable<KPI.MappedAssetNotation[]> {
    return combineLatest([
      this.loadInvestmentNotationsByAssets(assets).pipe(first()),
      this.usersService.allUsers$,
    ]).pipe(
      map(([notations, users]) =>
        notations.filter((notation) =>
          notation.audit_question_object.mnemonic === 'technical_state',
        ).map((notation) => ({
          ...notation,
          user_object: users.find((u) => u.get_user_id === notation.user),
        })).sort((a, b) =>
          a.audit_year - b.audit_year,
        ),
      ),
    );
  }

  public getLastAssetNotation(asset: KPI.MappedAsset): Observable<KPI.MappedAssetNotation> {
    return this.loadInvestmentNotationsByAssets([asset]).pipe(
      first(),
      map((notations) =>
        notations.filter((notation) =>
          notation.audit_question_object.mnemonic === 'technical_state',
        ).sort((a, b) => a.audit_year - b.audit_year).pop()
      )
    )
  }

}
